import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import ActionsGrid from 'components/ActionsGrid'
import CreateOrganizationModal from 'components/CreateOrganizationModal'
import EditOrganizationModal from 'components/EditOrganizationModal'
import DeleteOrganizationModal from 'components/DeleteOrganizationModal/DeleteOrganizationModal'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { usePermissions } from 'hooks/usePermissions'
import { selectIsLoadingOrganizations, selectOrganizations, setIsInitialized, addAlert } from 'store/global'
import {
  selectDisplayedOrganization,
  setDisplayedOrganization,
  createOrganization,
  editOrganization,
  deleteOrganization,
  closeDisplayedOrganization,
} from 'store/organizations'
import messages from './messages'
import styles from './Organizations.module.scss'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import Input from 'components/Input'
import { orderBy } from 'lodash'
import { saveAs } from 'file-saver'
import { Parser } from 'json2csv'

import { gridColumns } from './specs'

export default function Organizations() {
  const dispatch = useDispatch()
  const permissions = usePermissions()

  const isLoading = useSelector(selectIsLoadingOrganizations)
  const organizations = useSelector(selectOrganizations)
  const displayedOrganization = useSelector(selectDisplayedOrganization)

  const [sortColumn, setSortColumn] = useState(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')

  const sortableColumns = new Set(['name', 'alias'])

  const onCloseModal = () => dispatch(closeDisplayedOrganization())

  useEffect(() => {
    onCloseModal()
    dispatch(setIsInitialized(true))
  }, [])

  const onSort = column => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const filteredOrganizations = React.useMemo(() => {
    if (!searchTerm) return organizations
    return organizations.filter(org =>
      Object.values(org).some(value => value?.toString().toLowerCase().includes(searchTerm.toLowerCase())),
    )
  }, [organizations, searchTerm])

  const sortedOrganizations = React.useMemo(() => {
    if (!sortColumn) return filteredOrganizations
    return orderBy(filteredOrganizations, [sortColumn], [sortOrder])
  }, [filteredOrganizations, sortColumn, sortOrder])

  const onSubmit = organization => {
    if (displayedOrganization.action === 'delete') {
      dispatch(deleteOrganization({ organizationId: organization.id }))
    } else if (displayedOrganization.action === 'create') {
      const domains = [{name: organization.domains}]
      dispatch(createOrganization({ organization: { name: organization.name, alias: organization.alias, domains } }))
    } else if (displayedOrganization.action === 'edit') {
      dispatch(editOrganization({ organization }))
    }

    dispatch(addAlert({ type: 'success', message: 'Organization Action Performed' }))
    dispatch(setDisplayedOrganization())
  }

  const onDisplayModal = (action = 'create', existingOrganization = {}) => {
    const nextDisplayedOrganization = {
      ...existingOrganization,
      action,
      isOpen: true,
    }

    dispatch(setDisplayedOrganization({ organization: nextDisplayedOrganization }))
  }

  const onExportCSV = () => {
    if (!sortedOrganizations.length) return
    const fields = Object.keys(sortedOrganizations[0])
    const parser = new Parser({ fields })
    const csv = parser.parse(sortedOrganizations)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'organizations.csv')
  }

  const renderGridCell = (organization, column) => {
    if (column === 'domains') {
      return organization.domains.map(domain => domain.name).join(', ')
    }
    if (column === 'actions') {
      if (!permissions.admin) {
        return null
      }
      return (
        <div className={styles.actions}>
          <Button color='primary' outline onClick={() => onDisplayModal('edit', organization)}>
            <Icon name='edit' size='regular'>
              <FormattedMessage id="app.containers.Organizations.actions.edit" defaultMessage="Edit" />
            </Icon>
          </Button>
          <Button color='danger' outline onClick={() => onDisplayModal('delete', organization)}>
            <Icon name='delete' size='regular'>
              <FormattedMessage id="app.containers.Organizations.actions.delete" defaultMessage="Delete" />
            </Icon>
          </Button>
        </div>
      )
    }
    return organization[column]
  }

  const renderModal = () => {
    if (!displayedOrganization.isOpen) return null

    switch (displayedOrganization.action) {
      case 'create':
        return <CreateOrganizationModal onSubmit={onSubmit} />
      case 'edit':
        return <EditOrganizationModal onSubmit={onSubmit} />
      case 'delete':
        return (
          <DeleteOrganizationModal organization={displayedOrganization} onSubmit={onSubmit} onClose={onCloseModal} />
        )
      default:
        return null
    }
  }

  return (
    <div className={styles.container}>
      <Input
        placeholder='Input search text...'
        value={searchTerm}
        onChange={value => setSearchTerm(value)}
        className={styles.search}
      />

      <ActionsGrid
        isLoading={isLoading}
        type='organization'
        rows={sortedOrganizations}
        columns={gridColumns}
        header={<FormattedMessage {...messages.header} />}
        onAction={onDisplayModal}
        onSort={onSort}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        showCreateButton={permissions.admin}
        onExportCSV={onExportCSV}
      >
        {(organization, columnKey, isHeader) =>
          isHeader ? (
            <div>
              {sortableColumns.has(columnKey) ? (
                <div className={styles.header} onClick={() => onSort(columnKey)}>
                  <FormattedMessage {...messages.columns[columnKey]} /> <SwapVertIcon fontSize='small' />
                </div>
              ) : (
                <FormattedMessage {...messages.columns[columnKey]} />
              )}
            </div>
          ) : (
            renderGridCell(organization, columnKey)
          )
        }
      </ActionsGrid>
      {renderModal()}
    </div>
  )
}
